import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi, apiClient } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import TendenciasChart from '../components/TendenciasChart';
import { 
  Building2, Users, Heart, TrendingUp, Bell, 
  ChevronRight, MapPin, Activity, ArrowUpRight,
  Shield, AlertTriangle, CheckCircle, XCircle,
  Package, MessageSquare, Building, FileText, AlertCircle
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';

const COLORS = ['#0066CC', '#00AA66', '#F59E0B', '#EF4444', '#8B5CF6'];

export default function DashboardPage() {
  const api = useApi();
  const navigate = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [alertas, setAlertas] = useState([]);
  const [metricasQualidade, setMetricasQualidade] = useState(null);
  const [portalStats, setPortalStats] = useState(null);
  const [docAlertas, setDocAlertas] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [kpisData, alertasData] = await Promise.all([
        api.getDashboardKPIs(),
        api.getAlertas({ status: 'Pendente', limit: 5 })
      ]);
      setKpis(kpisData);
      setAlertas(alertasData);
      
      // Carregar métricas de qualidade
      try {
        const qualidadeRes = await apiClient.get('/dashboard/metricas-qualidade');
        setMetricasQualidade(qualidadeRes.data);
      } catch (e) {
        console.error('Erro ao carregar métricas de qualidade:', e);
      }
      
      // Carregar stats do portal (Gestão Pedidos)
      try {
        const portalRes = await apiClient.get('/admin/portal/stats');
        setPortalStats(portalRes.data);
      } catch (e) {
        console.error('Erro ao carregar stats do portal:', e);
      }

      // Carregar alertas de documentos
      try {
        const docRes = await apiClient.get('/admin/documentos-aviso');
        setDocAlertas(docRes.data);
      } catch (e) {
        console.error('Erro ao carregar alertas de documentos:', e);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="dashboard-loading">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  const faixaEtariaData = kpis?.utentes_por_faixa_etaria ? [
    { name: 'Bebés (0-2)', value: kpis.utentes_por_faixa_etaria.bebes_0_2 || 0 },
    { name: 'Crianças (3-10)', value: kpis.utentes_por_faixa_etaria.criancas_3_10 || 0 },
    { name: 'Jovens (11-18)', value: kpis.utentes_por_faixa_etaria.jovens_11_18 || 0 },
    { name: 'Adultos (18-64)', value: kpis.utentes_por_faixa_etaria.adultos_18_64 || 0 },
    { name: 'Idosos (65+)', value: kpis.utentes_por_faixa_etaria.idosos_65plus || 0 },
  ] : [];

  const generoData = kpis?.utentes_por_genero ? [
    { name: 'Masculino', value: kpis.utentes_por_genero.masculino || 0 },
    { name: 'Feminino', value: kpis.utentes_por_genero.feminino || 0 },
  ] : [];

  const distritosChartData = (kpis?.instituicoes_por_distrito || [])
    .slice(0, 8)
    .map(d => ({ name: d.distrito, value: d.count }));

  // Dados para gráfico de qualidade
  const qualidadeChartData = metricasQualidade ? [
    { name: 'Excelente', value: metricasQualidade.distribuicao_qualidade?.excelente || 0, color: '#22C55E' },
    { name: 'Bom', value: metricasQualidade.distribuicao_qualidade?.bom || 0, color: '#3B82F6' },
    { name: 'Atenção', value: metricasQualidade.distribuicao_qualidade?.atencao || 0, color: '#F59E0B' },
    { name: 'Desatualizado', value: metricasQualidade.distribuicao_qualidade?.desatualizado || 0, color: '#EF4444' },
  ] : [];

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
        <p className="text-slate-500 mt-1">Visão geral da rede de instituições ENTRAJUDA</p>
      </div>

      {/* KPI Cards - Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card 
          className="card-metric group cursor-pointer hover:shadow-lg transition-all hover:border-[#0066CC]/30" 
          data-testid="kpi-instituicoes"
          onClick={() => navigate('/instituicoes')}
        >
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Instituições Ativas</p>
                <p className="text-3xl font-bold text-slate-900 dark:text-white mt-2">
                  {kpis?.total_instituicoes_ativas?.toLocaleString('pt-PT') || 0}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  de {kpis?.total_instituicoes?.toLocaleString('pt-PT') || 0} totais
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-[#0066CC]/10 flex items-center justify-center group-hover:bg-[#0066CC]/20 transition-colors">
                <Building2 className="w-6 h-6 text-[#0066CC]" />
              </div>
            </div>
            <div className="mt-3 flex items-center text-xs text-[#0066CC] opacity-0 group-hover:opacity-100 transition-opacity">
              <span>Ver instituições</span>
              <ChevronRight className="w-3 h-3 ml-1" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="card-metric group cursor-pointer hover:shadow-lg transition-all hover:border-[#00AA66]/30" 
          data-testid="kpi-utentes"
          onClick={() => navigate('/utentes')}
        >
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Pessoas Apoiadas</p>
                <p className="text-3xl font-bold text-slate-900 dark:text-white mt-2">
                  {kpis?.total_utentes?.toLocaleString('pt-PT') || 0}
                </p>
                <p className="text-xs text-[#00AA66] mt-1 flex items-center gap-1">
                  <ArrowUpRight className="w-3 h-3" />
                  Dados agregados RGPD
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-[#00AA66]/10 flex items-center justify-center group-hover:bg-[#00AA66]/20 transition-colors">
                <Users className="w-6 h-6 text-[#00AA66]" />
              </div>
            </div>
            <div className="mt-3 flex items-center text-xs text-[#00AA66] opacity-0 group-hover:opacity-100 transition-opacity">
              <span>Ver utentes</span>
              <ChevronRight className="w-3 h-3 ml-1" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="card-metric group cursor-pointer hover:shadow-lg transition-all hover:border-purple-500/30" 
          data-testid="kpi-voluntarios"
          onClick={() => navigate('/voluntariado')}
        >
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Voluntários</p>
                <p className="text-3xl font-bold text-slate-900 dark:text-white mt-2">
                  {kpis?.total_voluntarios?.toLocaleString('pt-PT') || 0}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  {kpis?.total_tecnicos?.toLocaleString('pt-PT') || 0} técnicos
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center group-hover:bg-purple-200 dark:group-hover:bg-purple-900/50 transition-colors">
                <Heart className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
            <div className="mt-3 flex items-center text-xs text-purple-600 dark:text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity">
              <span>Ver voluntários</span>
              <ChevronRight className="w-3 h-3 ml-1" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="card-metric group cursor-pointer hover:shadow-lg transition-all hover:border-amber-500/30" 
          data-testid="kpi-valencias"
          onClick={() => navigate('/instituicoes')}
        >
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Respostas Sociais</p>
                <p className="text-3xl font-bold text-slate-900 dark:text-white mt-2">
                  {kpis?.total_valencias_ativas?.toLocaleString('pt-PT') || 0}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  respostas sociais
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center group-hover:bg-amber-200 dark:group-hover:bg-amber-900/50 transition-colors">
                <Activity className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
            </div>
            <div className="mt-3 flex items-center text-xs text-amber-600 dark:text-amber-400 opacity-0 group-hover:opacity-100 transition-opacity">
              <span>Ver respostas sociais</span>
              <ChevronRight className="w-3 h-3 ml-1" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gestão de Pedidos - Dashboard Resumido */}
      {portalStats && (
        <Card className="cursor-pointer hover:shadow-lg transition-all hover:border-[#0066CC]/30" onClick={() => navigate('/gestao-pedidos')} data-testid="gestao-pedidos-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Package className="w-5 h-5 text-[#0066CC]" />
                Gestão de Pedidos
              </CardTitle>
              <Button variant="ghost" size="sm" className="text-[#0066CC]" onClick={(e) => { e.stopPropagation(); navigate('/gestao-pedidos'); }}>
                Ver todos <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div 
                className={`p-4 rounded-lg cursor-pointer transition-all hover:scale-105 ${portalStats.pedidos?.pendentes > 0 ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'}`}
                onClick={(e) => { e.stopPropagation(); navigate('/pedidos-alteracao'); }}
                data-testid="pedidos-pendentes-link"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-amber-600">{portalStats.pedidos?.pendentes || 0}</p>
                    <p className="text-xs text-slate-500">Pedidos Pendentes</p>
                  </div>
                  <Package className="w-6 h-6 text-amber-500 opacity-60" />
                </div>
              </div>
              <div 
                className="p-4 rounded-lg bg-slate-50 cursor-pointer transition-all hover:scale-105 hover:bg-green-50"
                onClick={(e) => { e.stopPropagation(); navigate('/admin/missoes'); }}
                data-testid="missoes-abertas-link"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-green-600">{portalStats.missoes?.abertas || 0}</p>
                    <p className="text-xs text-slate-500">Missões Abertas</p>
                  </div>
                  <Heart className="w-6 h-6 text-pink-500 opacity-60" />
                </div>
              </div>
              <div 
                className={`p-4 rounded-lg cursor-pointer transition-all hover:scale-105 ${portalStats.mensagens?.abertas > 0 ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50'}`}
                onClick={(e) => { e.stopPropagation(); navigate('/admin/notificacoes'); }}
                data-testid="tickets-abertos-link"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{portalStats.mensagens?.abertas || 0}</p>
                    <p className="text-xs text-slate-500">Tickets Abertos</p>
                  </div>
                  <MessageSquare className="w-6 h-6 text-blue-500 opacity-60" />
                </div>
              </div>
              <div 
                className="p-4 rounded-lg bg-slate-50 cursor-pointer transition-all hover:scale-105 hover:bg-purple-50"
                onClick={(e) => { e.stopPropagation(); navigate('/candidaturas'); }}
                data-testid="candidaturas-pendentes-link"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-purple-600">{portalStats.voluntarios?.candidaturas_pendentes || 0}</p>
                    <p className="text-xs text-slate-500">Candidaturas Vol.</p>
                  </div>
                  <Users className="w-6 h-6 text-purple-500 opacity-60" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Card de Alertas de Documentos */}
      {docAlertas && (docAlertas.expirados > 0 || docAlertas.criticos > 0 || docAlertas.aviso > 0) && (
        <Card
          className={`cursor-pointer hover:shadow-lg transition-all border-2 ${
            docAlertas.expirados > 0
              ? 'border-red-300 bg-red-50/40 dark:bg-red-900/10'
              : docAlertas.criticos > 0
              ? 'border-orange-300 bg-orange-50/40 dark:bg-orange-900/10'
              : 'border-amber-200 bg-amber-50/40 dark:bg-amber-900/10'
          }`}
          onClick={() => navigate('/candidaturas')}
          data-testid="card-documentos-alerta"
        >
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className={`text-lg font-semibold flex items-center gap-2 ${
                docAlertas.expirados > 0 ? 'text-red-700 dark:text-red-400'
                : docAlertas.criticos > 0 ? 'text-orange-700 dark:text-orange-400'
                : 'text-amber-700 dark:text-amber-400'
              }`}>
                <AlertCircle className="w-5 h-5" />
                Documentos em Alerta
              </CardTitle>
              <Badge className={
                docAlertas.expirados > 0 ? 'bg-red-500'
                : docAlertas.criticos > 0 ? 'bg-orange-500'
                : 'bg-amber-500'
              }>
                {docAlertas.total} doc(s)
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center p-3 bg-red-100 dark:bg-red-900/20 rounded-lg">
                <p className="text-2xl font-bold text-red-600">{docAlertas.expirados}</p>
                <p className="text-xs text-red-600 font-medium">Expirados</p>
              </div>
              <div className="text-center p-3 bg-orange-100 dark:bg-orange-900/20 rounded-lg">
                <p className="text-2xl font-bold text-orange-600">{docAlertas.criticos}</p>
                <p className="text-xs text-orange-600 font-medium">Críticos (≤7d)</p>
              </div>
              <div className="text-center p-3 bg-amber-100 dark:bg-amber-900/20 rounded-lg">
                <p className="text-2xl font-bold text-amber-600">{docAlertas.aviso}</p>
                <p className="text-xs text-amber-600 font-medium">A expirar</p>
              </div>
            </div>
            {docAlertas.avisos?.length > 0 && (
              <div className="space-y-1.5">
                {docAlertas.avisos.slice(0, 4).map((doc, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs py-1.5 px-2 rounded bg-white/70 dark:bg-slate-800/70 border border-slate-100 dark:border-slate-700">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${
                      doc.urgencia === 'expirado' ? 'bg-red-500'
                      : doc.urgencia === 'critico' ? 'bg-orange-500'
                      : 'bg-amber-400'
                    }`} />
                    <span className="font-medium truncate flex-1 dark:text-slate-200">{doc.nome}</span>
                    {doc.instituicao_nome && (
                      <span className="text-slate-400 truncate max-w-[120px]">{doc.instituicao_nome}</span>
                    )}
                    <span className={`font-mono shrink-0 ${
                      doc.urgencia === 'expirado' ? 'text-red-600'
                      : doc.urgencia === 'critico' ? 'text-orange-600'
                      : 'text-amber-600'
                    }`}>
                      {doc.dias_restantes < 0
                        ? `há ${Math.abs(doc.dias_restantes)}d`
                        : `${doc.dias_restantes}d`}
                    </span>
                  </div>
                ))}
                {docAlertas.avisos.length > 4 && (
                  <p className="text-xs text-slate-500 text-center pt-1">
                    +{docAlertas.avisos.length - 4} mais...
                  </p>
                )}
              </div>
            )}
            <div className="flex items-center text-xs text-slate-500 mt-3 gap-1">
              <FileText className="w-3 h-3" />
              <span>Configurar limiar em Configurações → Definições</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Institutions by District - Now full width on left */}
        <Card className="lg:col-span-2" data-testid="chart-distritos">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#0066CC]" />
                Instituições por Distrito
              </CardTitle>
              <Badge variant="outline" className="text-xs">Top 8 distritos</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={distritosChartData} layout="vertical" margin={{ left: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" tick={{ fill: '#64748B', fontSize: 12 }} />
                  <YAxis 
                    dataKey="name" 
                    type="category" 
                    tick={{ fill: '#64748B', fontSize: 12 }}
                    width={75}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Bar dataKey="value" fill="#0066CC" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Alerts Panel */}
        <Card data-testid="alerts-panel">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Bell className="w-5 h-5 text-amber-500" />
                Alertas
              </CardTitle>
              {kpis?.alertas_pendentes > 0 && (
                <Badge className="bg-amber-500">{kpis.alertas_pendentes}</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {alertas.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">
                  Sem alertas pendentes
                </p>
              ) : (
                alertas.map((alerta) => (
                  <div
                    key={alerta.id}
                    className="p-3 rounded-lg border border-slate-100 dark:border-slate-700 hover:border-[#0066CC] dark:hover:border-[#0066CC] hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors cursor-pointer"
                    data-testid={`alert-${alerta.id}`}
                    onClick={() => alerta.link_to ? navigate(alerta.link_to) : navigate('/alertas')}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full mt-2 ${
                        alerta.prioridade === 'Crítica' ? 'bg-red-500' :
                        alerta.prioridade === 'Alta' ? 'bg-orange-500' :
                        alerta.prioridade === 'Média' ? 'bg-yellow-500' : 'bg-blue-500'
                      }`}></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                          {alerta.titulo}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">
                          {alerta.descricao || alerta.mensagem}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            <Button variant="ghost" className="w-full mt-4 text-[#0066CC]" onClick={() => navigate('/alertas')}>
              Ver todos os alertas
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Users by Age Group */}
        <Card data-testid="chart-faixa-etaria">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold">Utentes por Faixa Etária</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={faixaEtariaData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {faixaEtariaData.map((entry, index) => (
                      <Cell key={`faixa-${entry.name}-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px'
                    }}
                    formatter={(value) => [value.toLocaleString('pt-PT'), 'Utentes']}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Gender Distribution */}
        <Card data-testid="chart-genero">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold">Distribuição por Género</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-8 justify-center py-6">
              {generoData.map((item, index) => (
                <div key={`genero-${item.name}-${index}`} className="text-center">
                  <div 
                    className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-3"
                    style={{ backgroundColor: index === 0 ? '#0066CC' : '#00AA66' }}
                  >
                    <span className="text-2xl font-bold text-white">
                      {((item.value / (generoData[0].value + generoData[1].value)) * 100).toFixed(0)}%
                    </span>
                  </div>
                  <p className="font-medium text-slate-900">{item.name}</p>
                  <p className="text-sm text-slate-500">{item.value.toLocaleString('pt-PT')} pessoas</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Métricas de Qualidade da Rede */}
      {metricasQualidade && (
        <div className="space-y-6">
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-[#0066CC]" />
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Qualidade da Informação da Rede</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Score Médio da Rede</p>
                    <p className="text-3xl font-bold text-slate-900 dark:text-white">{metricasQualidade.score_medio_rede}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <Progress value={metricasQualidade.score_medio_rede} className="h-2 mt-3" />
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-green-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Taxa Resposta Campanha {metricasQualidade.campanha_ano}</p>
                    <p className="text-3xl font-bold text-slate-900 dark:text-white">{metricasQualidade.taxa_resposta_campanha}%</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  {metricasQualidade.responderam_campanha} de {metricasQualidade.total_instituicoes} instituições
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-amber-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Precisam Atenção</p>
                    <p className="text-3xl font-bold text-amber-600">
                      {(metricasQualidade.distribuicao_qualidade?.atencao || 0) + (metricasQualidade.distribuicao_qualidade?.desatualizado || 0)}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">Instituições com dados desatualizados</p>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-slate-400">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Inativas</p>
                    <p className="text-3xl font-bold text-slate-500">{metricasQualidade.total_inativas || 0}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                    <Building className="w-6 h-6 text-slate-500" />
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-2">Instituições não ativas</p>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-purple-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Total Ativas</p>
                    <p className="text-3xl font-bold text-slate-900 dark:text-white">{metricasQualidade.total_instituicoes}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Gráfico de Distribuição de Qualidade */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-semibold">Distribuição por Qualidade</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={qualidadeChartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {qualidadeChartData.map((entry, index) => (
                          <Cell key={`qualidade-${entry.name}-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Lista de Instituições que Precisam Atenção */}
            <Card>
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-red-500" />
                    Instituições que Precisam de Atenção Urgente
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-[220px] overflow-y-auto">
                  {metricasQualidade.instituicoes_atencao_urgente?.length === 0 ? (
                    <div className="text-center py-8 text-green-600">
                      <CheckCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Todas as instituições estão atualizadas!</p>
                    </div>
                  ) : (
                    metricasQualidade.instituicoes_atencao_urgente?.map(inst => (
                      <div 
                        key={inst.id}
                        className="flex items-center justify-between p-3 rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 hover:border-red-300 cursor-pointer transition-colors"
                        onClick={() => navigate(`/instituicoes/${inst.id}`)}
                      >
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white">{inst.nome}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">
                              Score: {inst.score}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {inst.percentagem_preenchimento}% campos
                            </Badge>
                            {!inst.campanha_respondida && (
                              <Badge className="bg-red-100 text-red-800 text-xs">
                                Campanha pendente
                              </Badge>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                    ))
                  )}
                </div>
                {metricasQualidade.instituicoes_atencao_urgente?.length > 0 && (
                  <Button 
                    variant="ghost" 
                    className="w-full mt-4 text-[#0066CC]" 
                    onClick={() => navigate('/instituicoes')}
                  >
                    Ver todas as instituições
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Analytics e Tendências */}
      <div className="mt-8" data-testid="tendencias-section">
        <TendenciasChart />
      </div>
    </div>
  );
}
